import { type FC } from 'react';
import classnames from 'classnames';
import { useIntl, defineMessages, type MessageDescriptor } from 'dibs-react-intl';
import { type SuggestionType, type TermType } from './shared/nftSearchBarConstants';
import { useDeviceType } from '../hooks/useDeviceType';
import { useRecentHistory } from '../../hooks/useRecentHistory';
import { NftSearchBarSection } from './NftSearchBarSection';
import { NftSearchBarTerm } from './NftSearchBarTerm';
import { RecentlyViewedLazy } from './RecentlyViewed/RecentlyViewedLazy';

import styles from './NftSearchBar.scss';

const titles: {
    [key: string]: MessageDescriptor;
} = defineMessages({
    RECENT: {
        id: 'nft.search.suggestions.recent.type',
        defaultMessage: 'Recent searches',
    },
    ITEMS: {
        id: 'nft.search.suggestions.items.type',
        defaultMessage: 'NFTs',
    },
    CREATORS: {
        id: 'nft.search.suggestions.creators.type',
        defaultMessage: 'Creators',
    },
    EXHIBITIONS: {
        id: 'nft.search.suggestions.exhibitions.type',
        defaultMessage: 'Exhibitions',
    },
});

type Props = {
    visible: boolean;
    suggestions: SuggestionType[];
    focusOnOverlayScroll: boolean;
    handleRowClick: (arg0: string, arg1: string) => void;
    cursorHighlightId?: string;
    canShowRecentlyViewed: boolean;
    overlayClassName: string;
};

export const NftSearchBarOverlay: FC<Props> = ({
    visible,
    suggestions,
    handleRowClick,
    cursorHighlightId,
    focusOnOverlayScroll,
    overlayClassName,
    canShowRecentlyViewed,
}) => {
    const intl = useIntl();
    const { isMobile } = useDeviceType();
    const { itemHistory } = useRecentHistory();

    const showRecentlyViewedItems = canShowRecentlyViewed && !!itemHistory.length;
    const hasSuggestions =
        suggestions.length > 0 &&
        suggestions.find(
            (suggestion: SuggestionType) => suggestion.terms && suggestion.terms.length
        );

    const overlayClasses = classnames(styles.overlay, {
        [overlayClassName]: !!overlayClassName,
        [styles.isVisible]: visible,
    });

    if (!hasSuggestions && !showRecentlyViewedItems) {
        return null;
    }

    return (
        <div
            aria-live={visible ? 'assertive' : 'off'}
            data-tn="search-bar-overlay"
            className={overlayClasses}
            onTouchStart={
                focusOnOverlayScroll
                    ? () => (document.activeElement as HTMLElement).blur()
                    : () => {}
            }
        >
            {isMobile && showRecentlyViewedItems && <RecentlyViewedLazy />}
            {suggestions.map((section: SuggestionType, index: number) => {
                const title = section.type || 'RECENT';
                const { terms, type } = section;
                if (!terms || !terms.length) {
                    return null;
                }
                return (
                    <NftSearchBarSection key={index} title={intl.formatMessage(titles[title])}>
                        {terms.map(
                            (
                                { term, subterm, urlLabel, image, defaultAvatar }: TermType,
                                jindex: number
                            ) => {
                                const href = urlLabel || '';
                                return (
                                    <NftSearchBarTerm
                                        key={jindex}
                                        id={`${term}-${type}-${jindex}`}
                                        title={section.type || ''}
                                        term={term || ''}
                                        subterm={subterm || ''}
                                        image={image}
                                        defaultAvatar={defaultAvatar}
                                        href={href}
                                        handleRowClick={handleRowClick}
                                        cursorHighlightId={cursorHighlightId}
                                    />
                                );
                            }
                        )}
                    </NftSearchBarSection>
                );
            })}
            {!isMobile && showRecentlyViewedItems && <RecentlyViewedLazy />}
        </div>
    );
};
